import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface SubsidieRegeling {
  id: string;
  code: string;
  jaar: number;
  naam: string;
  korte_naam: string;
  type: 'subsidie' | 'fiscaal';
  status: 'open' | 'gesloten' | 'uitgeput' | 'gepland' | 'voorbehoud';
  aanvraag_open: string | null;
  aanvraag_sluit: string | null;
  concept_vanaf: string | null;
  budget_totaal: number | null;
  budget_restant: number | null;
  budget_toelichting: string | null;
  max_subsidie_per_aanvrager: number | null;
  min_subsidiebedrag: number | null;
  subsidie_percentage_max: number | null;
  rvo_url: string | null;
  opmerkingen: string | null;
  samenvatting: string | null;
}

export interface SubsidieRegel {
  id: string;
  regeling_id: string;
  veld: string;
  operator: string;
  waarde: any;
  verplicht: boolean;
  beschrijving: string;
  foutmelding: string | null;
  groep: string | null;
  sort_order: number;
}

export interface SubsidieBedrag {
  id: string;
  regeling_id: string;
  categorie: string;
  subcategorie: string | null;
  bedrag_type: 'vast' | 'per_kwh' | 'per_kw' | 'percentage' | 'aftrek';
  bedrag: number;
  max_bedrag: number | null;
  max_eenheden: number | null;
  min_eenheden: number | null;
  bedrijfsgrootte: string | null;
  opmerkingen: string | null;
}

export interface SubsidieCombinatie {
  id: string;
  regeling_a: string;
  regeling_b: string;
  jaar: number;
  combineerbaar: boolean;
  toelichting: string | null;
}

export interface SubsidieDocument {
  id: string;
  regeling_id: string;
  document_naam: string;
  beschrijving: string | null;
  verplicht: boolean;
  sort_order: number;
  /**
   * Sinds migratie 069. De query hierboven haalt `*`, dus deze velden komen
   * vanzelf mee; ze staan hier zodat het scherm ze mag lezen.
   *
   * `verplicht` alleen is niet genoeg meer: drie eisen zijn *voorwaardelijk*
   * (alleen bij een batterij, alleen boven 100 kW, alleen op andermans terrein)
   * en tellen als verplicht zodra hun voorwaarde geldt. Wie alleen de boolean
   * afdrukt maakt van "verplicht als je een batterij aanvraagt" een kale
   * "(verplicht)".
   */
  soort?: 'verplicht' | 'voorwaardelijk' | 'aanbevolen';
  voorwaarde?: string | null;
  bron_url?: string | null;
  bron_gecontroleerd_op?: string | null;
}

export function useSubsidieRegelingen(jaar = 2026) {
  const regelingenQuery = useQuery({
    queryKey: ['sub_regelingen', jaar],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sub_regelingen')
        .select('*')
        .eq('jaar', jaar)
        .order('code');
      if (error) throw error;
      return data as SubsidieRegeling[];
    },
  });

  const regelsQuery = useQuery({
    queryKey: ['sub_regels', jaar],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sub_regels')
        .select('*, sub_regelingen!inner(jaar)')
        .eq('sub_regelingen.jaar', jaar)
        .order('sort_order');
      if (error) throw error;
      return data as SubsidieRegel[];
    },
    enabled: regelingenQuery.isSuccess,
  });

  const bedragenQuery = useQuery({
    queryKey: ['sub_bedragen', jaar],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sub_bedragen')
        .select('*, sub_regelingen!inner(jaar)')
        .eq('sub_regelingen.jaar', jaar)
        .order('sort_order');
      if (error) throw error;
      return data as SubsidieBedrag[];
    },
    enabled: regelingenQuery.isSuccess,
  });

  const combinatiesQuery = useQuery({
    queryKey: ['sub_combinaties', jaar],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sub_combinaties')
        .select('*')
        .eq('jaar', jaar);
      if (error) throw error;
      return data as SubsidieCombinatie[];
    },
  });

  const documentenQuery = useQuery({
    queryKey: ['sub_documenten', jaar],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sub_documenten')
        .select('*, sub_regelingen!inner(jaar)')
        .eq('sub_regelingen.jaar', jaar)
        .order('sort_order');
      if (error) throw error;
      return data as SubsidieDocument[];
    },
    enabled: regelingenQuery.isSuccess,
  });

  return {
    regelingen: regelingenQuery.data ?? [],
    regels: regelsQuery.data ?? [],
    bedragen: bedragenQuery.data ?? [],
    combinaties: combinatiesQuery.data ?? [],
    documenten: documentenQuery.data ?? [],
    isLoading: regelingenQuery.isLoading,
    error: regelingenQuery.error,
  };
}
